import { runBatch } from '../../../utils/office/document';
import { splitEdgeSpacing } from '../../../utils/office/selection';

/** Word highlight names used for annotations. Exported so the pane buttons can
 *  render themselves in the colour they actually insert — a preview that cannot
 *  drift from the insert because both read this. */
export const ANNOTATION_HIGHLIGHT = 'Yellow';
export const COMPANY_HIGHLIGHT = 'Lime';

/**
 * Inserts a bracketed note with formatted prefix.
 *
 * Caret behavior (issue #4):
 *  - EMPTY invocation (no selection, no override) → the template `[V14: .]`
 *    with the caret placed BEFORE the dot. The dot nudges the house style
 *    (annotations end with a period) and is already body-formatted; because
 *    typed text inherits the formatting of the character before the caret,
 *    the prefix's trailing space carries BODY formatting (italic, highlighted,
 *    NOT bold) — so what the user types comes out styled like annotation text.
 *  - With a selection (or override text) → the wrapped annotation, caret AFTER
 *    the closing bracket so the user continues in the document.
 */
export async function insertBracketedNote(
  prefix: string,
  textOverride?: string,
  highlightColor: string = ANNOTATION_HIGHLIGHT,
): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    selection.load('text');
    await context.sync();

    // Spacing the user selected along with the text is kept OUTSIDE the
    // annotation: it is re-emitted around the brackets instead of being
    // replaced away (see splitEdgeSpacing). The body always comes from the
    // trimmed text, the spacing always from the live selection — with a
    // textOverride the caller passes the already-trimmed selection text.
    const { lead, core, trail } = splitEdgeSpacing(selection.text ?? '');
    const body = (textOverride ?? core).trim();

    const openBracket = selection.insertText(`${lead}[`, Word.InsertLocation.replace);
    openBracket.font.bold = false;
    openBracket.font.italic = false;
    openBracket.font.highlightColor = '';

    // Prefix WITHOUT its trailing space — the space is body-formatted (see doc).
    const prefixCore = prefix.replace(/\s+$/, '');
    const prefixRange = openBracket.insertText(prefixCore, Word.InsertLocation.after);
    prefixRange.font.bold = true;
    prefixRange.font.italic = true;
    prefixRange.font.highlightColor = highlightColor;

    const spaceRange = prefixRange.insertText(' ', Word.InsertLocation.after);
    spaceRange.font.bold = false;
    spaceRange.font.italic = true;
    spaceRange.font.highlightColor = highlightColor;

    let tail: Word.Range = spaceRange;
    let caretBeforeDot: Word.Range | null = null;

    if (body) {
      const textRange = spaceRange.insertText(body, Word.InsertLocation.after);
      textRange.font.bold = false;
      textRange.font.italic = true;
      textRange.font.highlightColor = highlightColor;
      tail = textRange;
    } else {
      const dotRange = spaceRange.insertText('.', Word.InsertLocation.after);
      dotRange.font.bold = false;
      dotRange.font.italic = true;
      dotRange.font.highlightColor = highlightColor;
      tail = dotRange;
      caretBeforeDot = dotRange;
    }

    const closeBracket = tail.insertText(`]${trail}`, Word.InsertLocation.after);
    closeBracket.font.bold = false;
    closeBracket.font.italic = false;
    closeBracket.font.highlightColor = '';

    // Caret placement (issue #4a/#4b).
    if (caretBeforeDot) caretBeforeDot.select(Word.SelectionMode.start);
    else closeBracket.select(Word.SelectionMode.end);

    await context.sync();
  });
}

export async function insertV14Comment(textOverride?: string): Promise<void> {
  return insertBracketedNote('V14: ', textOverride);
}

export async function insertNoteToDraft(textOverride?: string): Promise<void> {
  return insertBracketedNote('NTD: ', textOverride);
}

export async function insertCompanyComment(textOverride?: string): Promise<void> {
  return insertBracketedNote('@Company: ', textOverride, COMPANY_HIGHLIGHT);
}

export async function insertCustomPrefix(
  prefix: string,
  textOverride?: string,
  highlightColor: string = ANNOTATION_HIGHLIGHT,
): Promise<void> {
  return insertBracketedNote(`${prefix}: `, textOverride, highlightColor);
}
